/****************
 * How an open position reads on screen.
 *
 * Extracted from OpenTradesTray so the decisions can be tested. Every function
 * here answers a question the component would otherwise answer inline, and each
 * one has a wrong answer that looks perfectly reasonable rendered: a size
 * rounded to 0.00, a shared badge on a position only one session holds, a
 * "matched to sessions" note fired by floating-point dust.
 *
 * The tray itself is then only layout, which is the part a test would struggle
 * to assert anything useful about.
 ****************/

import type { OpenPositionRow } from '@/api/client';
import { intlLocale, fmtUsd, format, dateTimeLines } from '@/lib/format';
import { formatPrice } from '@/lib/utils';

/** Signed dollars, always two decimals, sign always shown for non-zero. */
export function money(n: number): string {
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return sign + fmtUsd(Math.abs(n), 2, { group: false });
}

/** Green for profit, red for loss, neutral for exactly flat. */
export function toneFor(n: number): string {
  if (n > 0) return 'text-emerald-500';
  if (n < 0) return 'text-red-500';
  return 'text-muted-foreground';
}

/**
 * Size, at the precision the number deserves.
 *
 * A position can be 0.004 BTC or 48,000 DOGE, and a fixed number of decimals
 * makes one of them unreadable: four decimals turns the DOGE line into noise,
 * two rounds the BTC line to 0.00 and states the user holds nothing.
 */
export function size(n: number): string {
  const abs = Math.abs(n);
  if (abs === 0) return '0';
  if (abs >= 1000) return n.toLocaleString(intlLocale(), { maximumFractionDigits: 0 });
  if (abs >= 1) return n.toLocaleString(intlLocale(), { maximumFractionDigits: 2 });
  return n.toLocaleString(intlLocale(), { maximumFractionDigits: 6 });
}

/**
 * What the row should say about attribution, if anything.
 *
 *  - 'unmatched'      the venue holds it; no session of ours claims it
 *  - 'venue-missing'  we hold it; the venue does not report it
 *  - 'partial'        some of the position is matched and some is not
 *  - null             fully accounted for; say nothing
 *
 * The threshold on 'partial' is deliberate. Sizes are floats arriving from two
 * systems, so an exactly-matched position routinely differs in the last
 * decimal; firing the note on any difference at all would put a permanent
 * warning under every healthy row and teach people to ignore it.
 */
export function attributionNote(
  row: Pick<OpenPositionRow, 'sessions' | 'size' | 'attributed_size' | 'venue_missing'>,
): 'unmatched' | 'venue-missing' | 'partial' | null {
  if (row.venue_missing) return 'venue-missing';
  // A position the venue holds with no session attributed has been sent as
  // null, not [] (2026-09-24), and reading .length of it blanked the tray.
  if ((row.sessions ?? []).length === 0) return 'unmatched';
  if (row.size > 0 && row.attributed_size < row.size * 0.99) return 'partial';
  return null;
}

/**
 * True when the row must name each session's portion.
 *
 * Guillaume, 2026-09-05: "if 2 sessions trade the same asset in the same
 * direction at same time, we should SPECIFY it and display what portions belong
 * to what session." The server sets `shared`; this refuses to render the split
 * UI without the sessions to fill it, so a bad payload shows a normal row
 * rather than an empty "Shared by 2 sessions" box.
 */
export function showsSplit(row: Pick<OpenPositionRow, 'shared' | 'sessions'>): boolean {
  return row.shared && (row.sessions ?? []).length > 1;
}

/****************
 * Whether our sessions' sizes add up to what the exchange is holding.
 *
 * While this is false, the per-session split is an assertion the exchange does
 * not support -- and a user is better off knowing than being shown a confident
 * wrong figure. The measured case: SNDKUSDT with two sessions each claiming
 * 0.28 against a broker position of 0.28 in total.
 *
 * "Not reported" counts as agreeing. An older backend that does not send the
 * field would otherwise light every shared row with a warning that means
 * nothing, which is how a real warning stops being read.
 ****************/
export function sizesDisagree(
  row: Pick<OpenPositionRow, 'basis_agrees'>,
): boolean {
  return row.basis_agrees === false;
}

/****************
 * AN OPEN POSITION HAS TWO PnLs; A CLOSED TRADE HAS ONE.
 *
 * G-21. `unrealized_pnl` is floating and still at risk. `realised_so_far` is
 * what the holder has already banked by partly closing — it appears only after
 * a partial close, and it survives the rest of the position going against them.
 *
 * Two rules, and both matter:
 *
 *   SHOW IT ONLY WHEN THERE IS ONE. A line reading $0.00 on every position
 *   nobody scaled out of invents a distinction where there is none, and teaches
 *   the reader to skip the line on the rows where it means something.
 *
 *   NEVER ADD THEM. Their total answers neither question a holder is asking —
 *   not "what is at risk" and not "what have I banked" — and it double-counts
 *   the banked half against any session total that already includes it.
 *
 * A function rather than an inline `> 0` so the rule is stated once and can be
 * tested; the sum is guarded by a scan, because a rule about what must NOT be
 * written cannot be expressed as a return value.
 ****************/
export function hasBankedPnl(realisedSoFar?: number): boolean {
  return Math.abs(realisedSoFar ?? 0) > 0;
}

/*
 * The black square in the header (Guillaume, 2026-09-30, and before). The tray
 * wrote `cancelError?.id === o.our_order_id`. With no cancel error the left
 * side is undefined; for an order CopyFi did not place (a stop set in the
 * Phemex app) the right side is undefined too. undefined === undefined, so it
 * went on to read null.message and the whole tray threw. 3 of 16 production
 * accounts held such an order on 2026-10-01. The message is returned only for
 * a real error on a real id.
 */
export function cancelErrorFor(
  err: { id: number; message: string } | null | undefined,
  orderId: number | null | undefined,
): string | null {
  if (!err || orderId === null || orderId === undefined) return null;
  return err.id === orderId ? err.message : null;
}

// True only for a reply the backend itself marked as the paper book.
export function isPaperBook(reply: unknown): boolean {
  return Boolean(reply) && (reply as { kind?: unknown }).kind === 'paper';
}

/****************
 * THE RECENTLY CLOSED ROWS, AND A LIQUIDATION PRICE THAT IS NOT ONE. 2026-10-01.
 *
 * Guillaume, from a screenshot of the tray:
 *
 *  - "For the recently closed, add the Time at which it was closed."
 *  - the closed rows printed the stored floats as they are:
 *    "15.061056555269923 → 14.397999999999998". An average entry is a division
 *    and keeps every digit of it. They go through formatPrice, the price rule
 *    the rest of the app uses.
 *  - "LIQ. PRICE 0.0001" on XRPUSDT and "0.001" on LINKUSDT, both cross longs.
 ****************/

/** "15.06106 → 14.39800", each side by the app's price rule; "—" for a price we do not have. */
export function closedPrices(entry: number | null | undefined, exit: number | null | undefined): string {
  const one = (p: number | null | undefined) => (p ? formatPrice(p) : '—');
  return `${one(entry)} → ${one(exit)}`;
}

/**
 * When a recently closed trade closed, short enough for the tray: "Sep 30,
 * 01:01" in the app's language and the device's time. No year: the list is
 * recent by definition, and the full date is the tooltip (closedAtTitle).
 * Empty when the close time is missing or unreadable, never "Invalid Date".
 */
export function closedAt(exitTime: string | null | undefined): string {
  const lines = dateTimeLines(exitTime);
  return lines ? format(new Date(exitTime as string), 'MMM d, HH:mm') : '';
}
export function closedAtTitle(exitTime: string | null | undefined): string {
  const lines = dateTimeLines(exitTime);
  return lines ? `${lines[0]}, ${lines[1]}` : '';
}

/**
 * What to show as a position's liquidation price.
 *
 *  - 'price'          the venue's number, a real boundary
 *  - 'none'           the venue reported none (0, absent)
 *  - 'wallet-backed'  the venue reported its PRICE FLOOR, which is not a boundary
 *
 * Measured on production, 2026-10-01: every cross LONG on the account read one
 * price tick as its liquidation price (XRP 0.0001, LINK / NEAR / AVAX 0.001,
 * each exactly the contract's smallest price step) while the cross SHORT beside
 * them read a real one (ZEC 2957.66 against a mark of 1321.84). On cross margin
 * the whole wallet backs the position; when it covers the position in full
 * there is no price above zero that liquidates it, and Phemex writes the lowest
 * price it can express. Printed as "LIQ. PRICE 0.0001" in the warning colour it
 * reads as a liquidation level.
 *
 * The tick size is not in the payload, so the test is on what a floor looks
 * like next to the price: a cross long whose liquidation price is under a
 * thousandth of its mark (a fall of more than 99.9%). A real boundary is never
 * there; a tick is, except on a coin worth a few ticks, where the number is
 * left as the venue gave it. Shorts and isolated positions are never rewritten.
 */
export type LiquidationReading =
  | { kind: 'price'; price: number }
  | { kind: 'none' }
  | { kind: 'wallet-backed' };

export function liquidationReading(row: {
  liquidation_price?: number | null;
  mark_price?: number | null;
  entry_price?: number | null;
  side?: string | null;
  margin_mode?: string | null;
}): LiquidationReading {
  const liq = Number(row.liquidation_price) || 0;
  if (!(liq > 0)) return { kind: 'none' };
  const cross = /^cross/i.test(String(row.margin_mode ?? '').trim());
  const long = /^(long|buy)$/i.test(String(row.side ?? '').trim());
  const ref = Number(row.mark_price) || Number(row.entry_price) || 0;
  if (cross && long && ref > 0 && liq <= ref * LIQUIDATION_FLOOR_RATIO) return { kind: 'wallet-backed' };
  return { kind: 'price', price: liq };
}
export const LIQUIDATION_FLOOR_RATIO = 0.001;
